import * as React from "react";
import { ButtonSize, StoreIcon, Collapse, ExpandIcon, IconButton, Text, TextVariant } from "../../ui";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";
import { PartOrder } from "../../../generated/nest-graphql";
import { PartOrderCard } from "../PartOrderCard";
import { PartsFilter, StoreGrouping } from "../partsStatus.utils";

/**
 * A store and everything to collect there. Store is the outer level because it is the
 * unit of work — a technician drives to a counter, not to an order. Starts open so the
 * whole day is visible; collapsing is for clearing a stop that's done.
 */
export const StoreGroup: React.FC<{
  group: StoreGrouping;
  filter: PartsFilter;
  // Controlled by the page so one button can expand or collapse every store at once.
  open: boolean;
  onToggle: () => void;
  onConfirmPickup: (order: PartOrder) => void;
  onReportIssue: (order: PartOrder) => void;
}> = ({ group, filter, open, onToggle, onConfirmPickup, onReportIssue }) => {
  const orderCount = group.orders.length;
  const reduceMotion = usePrefersReducedMotion();
  const duration = reduceMotion ? 0 : 220;

  return (
    <section className="mb-4 border border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-900 text-left ${
          open ? "border-b border-gray-700" : ""
        }`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <StoreIcon style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
            <Text variant={TextVariant.SUBTITLE1} style={{ fontWeight: 700 }}>
              {group.name}
            </Text>
          </span>
          {group.address && (
            <Text variant={TextVariant.BODY2} className="text-gray-400">
              {group.address}
            </Text>
          )}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {orderCount} {orderCount === 1 ? "order" : "orders"}
          </span>
          <IconButton size={ButtonSize.SMALL} as="span" tabIndex={-1} aria-hidden="true">
            <ExpandIcon
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: `transform ${duration}ms ease`,
              }}
            />
          </IconButton>
        </span>
      </button>

      <Collapse open={open} durationMs={duration} unmountOnClose>
        <div className="flex flex-col gap-2 p-2 bg-[#080c14]">
          {group.orders.map((order) => (
            <PartOrderCard
              key={order.id}
              order={order}
              filter={filter}
              onConfirmPickup={onConfirmPickup}
              onReportIssue={onReportIssue}
            />
          ))}
        </div>
      </Collapse>
    </section>
  );
};
